import React from 'react';
import { SONG_SLOT_MAX_LENGTH } from '@soundxyz/utils/const';
import { FadeAnimation } from '@components/basics/Animation/FadeAnimation';
import { UserCommentItem } from '@components/main-components/user/UserCommentItem';
import { UserCommentReplies } from '@components/main-components/user/UserCommentReplies';
import { useAuth } from '@context/auth/base';
import { CommentAuthorFragmentDoc, getFragment, type SongViewFragment } from '@graphql/reactQuery';
import { useLexicalValidate } from '@hooks/useLexicalValidate';
import { usePlaybackRange } from '@hooks/usePlaybackRange';
import { formatSlotToDisplayTimestamp } from '@utils/helpers';
import { CommentAction } from './CommentAction';
import type { Comment } from './store';

export const CommentItem = ({
  item,
  releaseFrag,
}: {
  item: Comment;
  releaseFrag: SongViewFragment;
}) => {
  const { authUser } = useAuth();
  const { postComment, normalizedSongSlot, nft } = item;
  const { artist } = getFragment(CommentAuthorFragmentDoc, postComment.author);
  const releaseId = releaseFrag.id;
  const releaseArtistId = releaseFrag.artist.id;
  const trackDuration = releaseFrag.track.duration;
  const isReleaseArtist = releaseArtistId === artist?.id;
  const isAuthUserComment = postComment.author.id === authUser?.id;
  const formatedSlotTimestamp = formatSlotToDisplayTimestamp(normalizedSongSlot, trackDuration);

  const { handleSlot } = usePlaybackRange({
    totalSlots: SONG_SLOT_MAX_LENGTH,
    trackId: releaseFrag.track.id,
  });

  const onSlotClick = () => {
    handleSlot(normalizedSongSlot);
  };

  const lexicalContent = useLexicalValidate(postComment.lexicalContent);

  return (
    <FadeAnimation transition={{ duration: 0.2 }}>
      <UserCommentItem
        postCommentId={postComment.id}
        userFragment={postComment.author}
        createdAt={postComment.createdAt}
        content={lexicalContent || postComment.content}
        showArtistTag={isReleaseArtist}
        showFeedCuratorBadge={false}
        showVerifiedBadge={!!artist}
        showCollectorTag={postComment.currentHolderOfRelease}
        selectedSlotInfo={{
          timestamp: formatedSlotTimestamp,
          onClick: onSlotClick,
        }}
        action={
          isAuthUserComment ? <CommentAction releaseId={releaseId} nftId={nft.id} /> : undefined
        }
        upvoteCount={postComment.upvoteCount}
      />

      <UserCommentReplies
        repliesCount={postComment.repliesCount}
        parentCommentId={postComment.id}
        postId={postComment.post.id}
      />
    </FadeAnimation>
  );
};
